// Tracks whether the local player is at the keyboard, in three stages (spec 2026-10-22):
//   awake    — normal play
//   zzz      — no input for IDLE_MS, or the tab was hidden: a Zzz over our head, seen by others
//   inactive — no input for INACTIVE_MS: main.js closes the game socket (others stop seeing us,
//              friends see us offline) and shows the grey "Przerwa" card (ui/inactiveOverlay.js)
// The pure transition logic (markActivity / setHidden / tick) is unit-tested; init() wires the
// browser input listeners and a 1s checker. `on(cb)` subscribers (the local avatar's Zzz and the
// realtime idle broadcast) hear only awake<->idle flips; `onStage(cb)` hears every stage change.
export const IDLE_MS = 60_000;       // 1 minute
export const INACTIVE_MS = 300_000;  // 5 minutes

// Only a real press brings an inactive player back: a nudged mouse or a scroll does not.
const DELIBERATE = new Set(['pointerdown', 'keydown', 'touchstart']);

export class IdleTracker {
  constructor(idleMs = IDLE_MS, inactiveMs = INACTIVE_MS) {
    this.idleMs = idleMs;
    this.inactiveMs = inactiveMs;
    this.stage = 'awake';
    this.suppressInactive = () => false;   // main.js: true while a Game Hall match is attached
    this._lastActivity = 0;
    this._listeners = new Set();
    this._stageListeners = new Set();
    this._timer = null;
  }

  get idle() { return this.stage !== 'awake'; }

  on(cb) { this._listeners.add(cb); return () => this._listeners.delete(cb); }
  onStage(cb) { this._stageListeners.add(cb); return () => this._stageListeners.delete(cb); }

  _set(stage) {
    if (stage === this.stage) return;
    const prev = this.stage;
    const wasIdle = this.idle;
    this.stage = stage;
    for (const cb of this._stageListeners) {
      try { cb(stage, prev); } catch (e) { console.error('idleTracker stage listener error:', e); }
    }
    if (this.idle !== wasIdle) {
      for (const cb of this._listeners) {
        try { cb(this.idle); } catch (e) { console.error('idleTracker listener error:', e); }
      }
    }
  }

  // Any input resets the clock and wakes — except that an inactive player needs a deliberate
  // press (pointerdown / keydown / touchstart); a mouse move or wheel is ignored entirely.
  markActivity(now, { deliberate = true } = {}) {
    if (this.stage === 'inactive' && !deliberate) return;
    this._lastActivity = now;
    this._set('awake');
  }

  // The tab was hidden (switched away, window minimised): Zzz at once. Coming back to the tab
  // does not wake by itself — the next input does. The activity clock is untouched.
  setHidden(hidden) {
    if (hidden && this.stage === 'awake') this._set('zzz');
  }

  // Periodic check. A late tick (laptop sleep, a throttled hidden tab) may jump awake → inactive.
  tick(now) {
    const quiet = now - this._lastActivity;
    if (this.stage !== 'inactive' && quiet >= this.inactiveMs && !this.suppressInactive()) {
      this._set('inactive');
    } else if (this.stage === 'awake' && quiet >= this.idleMs) {
      this._set('zzz');
    }
  }

  // Browser wiring (no-op effect in unit tests, which drive the methods directly).
  init() {
    if (this._timer) return;          // already initialised
    this._lastActivity = Date.now();
    const onActivity = (e) => this.markActivity(Date.now(), { deliberate: DELIBERATE.has(e.type) });
    for (const ev of ['pointermove', 'pointerdown', 'keydown', 'wheel', 'touchstart']) {
      window.addEventListener(ev, onActivity, { passive: true });
    }
    document.addEventListener('visibilitychange', () => this.setHidden(document.hidden));
    if (document.hidden) this.setHidden(true);
    this._timer = setInterval(() => this.tick(Date.now()), 1000);
  }
}

export const idleTracker = new IdleTracker();
